import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  TextField,
  Autocomplete,
  CircularProgress,
  Button,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import AuthService from '../../services/AuthService';
import { AuthContext } from '../../providers/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Type based on Keycloak UserRepresentation
export interface UserRepresentation {
  id: string;
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  enabled?: boolean;
  [key: string]: any; // allow other fields from Keycloak
}

export default function UserSearchWithImpersonate() {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<UserRepresentation[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserRepresentation | null>(null);
  const [impersonating, setImpersonating] = useState(false);

  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const { updateTokens, hasResourceAccess, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const [t] = useTranslation();

  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(async () => {
      if (!inputValue) {
        setOptions([]);
        return;
      }
      setLoading(true);
      try {
        const users: UserRepresentation[] = await AuthService.getUsers({
          search: `*${inputValue}*`,
        });
        setOptions(users);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }, 500); // 500ms debounce

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [inputValue]);

  const handleImpersonate = async () => {
    if (!selectedUser) return;
    setImpersonating(true);
    try {
      const tokens = await AuthService.impersonate(selectedUser.id);
      await updateTokens(tokens.access_token, tokens.refresh_token, true);
      await navigate('/');
    } catch (err) {
      console.error('Impersonation failed:', err);
    } finally {
      setImpersonating(false);
    }
  };

  if (!hasResourceAccess('realm-management', 'impersonation')) {
    return <></>;
  }
  return (
    <>
      <Typography
        variant="h5"
        sx={{ mt: user ? theme.spacing(4) : theme.spacing(0) }}
      >
        {t('impersonation')}
      </Typography>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ marginTop: '1rem', maxWidth: '50%' }}
      >
        <Autocomplete
          sx={{ flex: 1 }}
          getOptionLabel={(option) =>
            `${option.firstName ?? ''} ${option.lastName ?? ''} (${option.email})`.trim()
          }
          options={options}
          loading={loading}
          onChange={(event, newValue) => setSelectedUser(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('impersonation_input_label')}
              placeholder={t('impersonation_input_placeholder')}
              onChange={(e) => setInputValue(e.target.value)}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress
                        color="inherit"
                        size={20}
                      />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedUser || impersonating}
          onClick={handleImpersonate}
        >
          {impersonating ? t('impersonating_in_progress') : t('impersonation_button_label')}
        </Button>
      </Stack>
    </>
  );
}
